import { api } from "../client"
import { API_ENDPOINTS } from "../config"

const NUMERIC_ID_REGEX = /^\d+$/

export interface Client {
  id: number
  identifier: string
  firstName: string
  lastName: string
  email: string
  phoneMobile?: string
  title?: string
  dateOfBirth?: string
  maritalStatus?: string
  occupation?: string
  employmentStatus?: string
  grossIncome?: number
  netIncome?: number
  livingSituation?: string
  street?: string
  houseNumber?: string
  addressAddition?: string
  postalCode?: string
  cityPlaceOfResidence?: string
  federalState?: string
  country?: string
  nationality?: string
  healthInsurance?: string
  nursingCareInsuranceStatutoryPrivate?: string
  supplementaryInsurance?: string
  taxClass?: string
  taxAssessment?: string
  churchTax?: string
  socialSecurityId?: string
  taxId?: string
  mainAccountNumber?: string
  account1?: string
  account2?: string
  createdAt: string
  updatedAt: string
}

export interface CreateClientRequest {
  firstName: string
  lastName: string
  email: string
  telephone: string
  address?: {
    street: string
    houseNumber: string
    postalCode: string
    city: string
    country: string
  }
}

export interface UpdateClientRequest extends Partial<CreateClientRequest> {
  id: string
}

export interface InviteClientRequest {
  email: string
  firstName: string
  lastName: string
  message?: string
}

export interface CreateClientInvitationRequest {
  label?: string
  clientId?: number
  identifier?: string
  expiresInDays?: number
}

export interface ClientInvitation {
  id: number
  clientId: number | null
  label: string | null
  token: string
  createdAt: string
  expiresAt: string
}

export interface ClientInvitationResponse {
  invitation: ClientInvitation
  link: string
}

export interface ClientListResponse {
  clients: Client[]
  pagination?: {
    total: number
    page: number
    limit: number
    pages: number
  }
  total?: number
  page?: number
  limit?: number
  pages?: number
}

export interface ClientStats {
  totalClients: number
  newThisMonth: number
  activeClients: number
  pendingInvitations: number
}

export interface CategoryField {
  id: number
  key: string
  label: string
  dataType: string
  order: number
  value?: string
  options?: string[]
  enumValues?: string[]
  identifier?: string
}

export interface Subcategory {
  id: number
  name: string
  slug: string
  fields: CategoryField[]
}

export interface Category {
  id: number
  name: string
  slug: string
  identifier?: string
  subcategories: Subcategory[]
}

export interface ClientDetailsResponse {
  categories: Category[]
  client: Client
}

export const clientsService = {
  async getClients(params?: {
    page?: number
    limit?: number
    search?: string
    sortBy?: string
    sortOrder?: "asc" | "desc"
  }) {
    const queryParams = new URLSearchParams()

    if (params?.page) {
      queryParams.append("page", params.page.toString())
    }
    if (params?.limit) {
      queryParams.append("limit", params.limit.toString())
    }
    if (params?.search) {
      queryParams.append("search", params.search)
    }
    if (params?.sortBy) {
      queryParams.append("sortBy", params.sortBy)
    }
    if (params?.sortOrder) {
      queryParams.append("sortOrder", params.sortOrder)
    }

    const endpoint = queryParams.toString()
      ? `${API_ENDPOINTS.CLIENTS.LIST}?${queryParams.toString()}`
      : API_ENDPOINTS.CLIENTS.LIST

    const response = await api.get<ClientListResponse>(endpoint)
    return { data: response.data, success: true }
  },

  async getClientById(id: string) {
    try {
      const isLikelyIdentifier =
        !NUMERIC_ID_REGEX.test(id) ||
        (NUMERIC_ID_REGEX.test(id) && Number.parseInt(id, 10).toString() !== id)

      const searchParams = isLikelyIdentifier ? { search: id } : undefined
      const clientsResponse = await this.getClients(searchParams)

      let clients: Client[] | undefined
      if (clientsResponse.success && clientsResponse.data) {
        let dataToCheck = clientsResponse.data

        if (
          typeof dataToCheck === "object" &&
          "data" in dataToCheck &&
          typeof (dataToCheck as { data: unknown }).data === "object"
        ) {
          dataToCheck = (dataToCheck as { data: unknown })
            .data as ClientListResponse
        }

        if (
          typeof dataToCheck === "object" &&
          "clients" in dataToCheck &&
          Array.isArray((dataToCheck as ClientListResponse).clients)
        ) {
          clients = (dataToCheck as ClientListResponse).clients
        } else if (Array.isArray(dataToCheck)) {
          clients = dataToCheck as Client[]
        }
      }

      if (clients && clients.length > 0) {
        const client = clients.find(
          (c: Client) => c.identifier === id || c.id.toString() === id
        )

        if (client) {
          try {
            const detailsResponse = await this.getClientDetails(
              client.id.toString()
            )
            if (detailsResponse.success && detailsResponse.data) {
              const detailsData =
                "data" in detailsResponse.data
                  ? detailsResponse.data.data
                  : detailsResponse.data
              const fullClient =
                detailsData &&
                typeof detailsData === "object" &&
                "client" in detailsData
                  ? (detailsData as { client: Client }).client
                  : (detailsData as Client)

              return {
                success: true,
                data: fullClient,
                message: "Client found"
              }
            }
            return {
              success: true,
              data: client,
              message: "Client found"
            }
          } catch {
            return {
              success: true,
              data: client,
              message: "Client found"
            }
          }
        }
      }
    } catch {
      // Failed to fetch clients list
    }

    const isNumericId = NUMERIC_ID_REGEX.test(id)
    const isTrueNumericId =
      isNumericId && Number.parseInt(id, 10).toString() === id

    if (isTrueNumericId) {
      const response = await api.get<Client>(
        API_ENDPOINTS.CLIENTS.GET_BY_ID(id)
      )
      return { data: response.data, success: true }
    }

    throw new Error(`Client with identifier "${id}" not found`)
  },

  async getClientDetails(clientId: string) {
    const endpoint = `${API_ENDPOINTS.CLIENTS.GET_DETAILS}?clientId=${clientId}`
    const response = await api.get<ClientDetailsResponse>(endpoint)
    return { data: response.data, success: true }
  },

  async deleteClient(id: string) {
    const response = await api.delete(API_ENDPOINTS.CLIENTS.DELETE(id))
    return { data: response.data, success: true }
  },

  async bulkDelete(ids: number[]) {
    const response = await api.post(API_ENDPOINTS.CLIENTS.BULK_DELETE, { ids })
    return { data: response.data, success: true }
  },

  async createClientInvitation(invitationData: CreateClientInvitationRequest) {
    const response = await api.post<ClientInvitationResponse>(
      API_ENDPOINTS.CLIENTS.CREATE_INVITATION,
      invitationData
    )
    return { data: response.data, success: true }
  },

  async uploadExcel(file: File) {
    const formData = new FormData()
    formData.append("excelFile", file)

    const response = await api.post<{ message?: string }>(
      API_ENDPOINTS.CLIENTS.UPLOAD_EXCEL,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    )
    return { data: response.data, success: true }
  },

  async getInvitationForm(token: string, categoryId?: number) {
    let endpoint = API_ENDPOINTS.CLIENTS.GET_INVITATION_FORM(token)
    if (categoryId !== undefined) {
      endpoint += `?categoryId=${categoryId}`
    }

    const response = await api.get<ClientDetailsResponse>(endpoint)
    return { data: response.data, success: true }
  },

  async saveInvitationStep(
    token: string,
    categoryId: number,
    stepData: Record<string, string>
  ) {
    const endpoint = API_ENDPOINTS.CLIENTS.SAVE_INVITATION_STEP(
      token,
      categoryId
    )

    const response = await api.post<{
      success: boolean
      message?: string
      errors?: Record<string, string[]>
    }>(endpoint, stepData)

    // Check if the response indicates failure
    if (
      response.data &&
      typeof response.data === "object" &&
      "success" in response.data &&
      response.data.success === false
    ) {
      // Return the error response so it can be handled by the caller
      return { data: response.data, success: false }
    }

    return { data: response.data, success: true }
  }
}
